import type { Base64 } from "@towni/common";
import { base64ToUtf8, utf8ToBase64 } from "@towni/common";
import type React from "react";
import { createContext, useCallback, useContext, useMemo } from "react";
import { useSearchParams } from "react-router";
import type { ZodSchema } from "zod";
import { z } from "zod";

type SerializableValue = string | number | boolean | object | undefined;

type UrlStateContextType = {
    getValue: <T extends SerializableValue = SerializableValue>(
        key: string,
    ) => T | undefined;
    setValue: <T extends SerializableValue = SerializableValue>(
        key: string,
        value: T,
    ) => void;
    setValues: (updates: Record<string, SerializableValue>) => void;
    clearParam: (key: string) => void;
    clearAll: () => void;
};

type UrlStateProviderProps = {
    children: React.ReactNode;
};

const UrlStateContext = createContext<UrlStateContextType | undefined>(
    undefined,
);

const serializeValue = (
    key: string,
    value: SerializableValue,
): string | undefined => {
    if (value === undefined) {
        return undefined;
    }

    if (typeof value === "boolean") {
        return value ? "true" : "false";
    }

    if (typeof value === "number") {
        return value.toString();
    }

    if (Array.isArray(value) || (typeof value === "object" && value !== null)) {
        const serialized = JSON.stringify(value);
        const base64Encoded = utf8ToBase64(serialized);

        // Check for URL length limitations
        if (base64Encoded.length > 1500) {
            // eslint-disable-next-line no-console
            console.warn(
                `URL state for key "${key}" is very large (${serialized.length} chars) and may cause issues`,
            );
        }
        // Prefix with __ to indicate it's a base64 encoded value
        return `__${base64Encoded}__`;
    }

    return String(value);
};

const UrlStateProvider = ({ children }: UrlStateProviderProps) => {
    const [searchParams, setSearchParams] = useSearchParams();

    const getValue = useCallback(
        <T extends SerializableValue = SerializableValue>(
            key: string,
        ): T | undefined => {
            const value = searchParams.get(key);
            if (value === null) {
                return undefined;
            }

            // Handle special case values
            if (value === "true") {
                return true as T;
            }
            if (value === "false") {
                return false as T;
            }

            // Try to parse as number
            if (/^-?\d+(\.\d+)?$/.test(value)) {
                const num = Number(value);
                if (!isNaN(num)) {
                    return num as T;
                }
            }

            try {
                if (value.startsWith("__") && value.endsWith("__")) {
                    // Decode base64 encoded value
                    const base64Value = value.slice(2, -2) as Base64;
                    const decoded = base64ToUtf8(base64Value);
                    const parsed = JSON.parse(decoded);
                    return parsed as T;
                }
                // Try to parse as JSON
                const parsed = JSON.parse(value);
                return parsed as T;
            } catch {
                // Return as string if not JSON
                return value as T;
            }
        },
        [searchParams],
    );

    const setValue = useCallback(
        <T extends SerializableValue = SerializableValue>(
            key: string,
            value: T,
        ) => {
            const params = new URLSearchParams(searchParams);

            if (value === undefined) {
                params.delete(key);
            } else {
                const serialized = serializeValue(key, value);
                if (serialized !== undefined) {
                    params.set(key, serialized);
                } else {
                    params.delete(key);
                }
            }

            setSearchParams(params, { replace: true });
        },
        [searchParams, setSearchParams],
    );

    const setValues = useCallback(
        (updates: Record<string, SerializableValue>) => {
            const params = new URLSearchParams(searchParams);

            Object.entries(updates).forEach(([key, value]) => {
                if (value === undefined) {
                    params.delete(key);
                } else {
                    const serialized = serializeValue(key, value);
                    if (serialized !== undefined) {
                        params.set(key, serialized);
                    } else {
                        params.delete(key);
                    }
                }
            });

            setSearchParams(params, { replace: true });
        },
        [searchParams, setSearchParams],
    );

    const clearParam = useCallback(
        (key: string) => {
            const params = new URLSearchParams(searchParams);
            params.delete(key);
            setSearchParams(params, { replace: true });
        },
        [searchParams, setSearchParams],
    );

    const clearAll = useCallback(() => {
        // Clear all params if no prefix was specified
        setSearchParams(new URLSearchParams(), { replace: true });
    }, [setSearchParams]);

    const contextValue = useMemo(
        () => ({ getValue, setValue, setValues, clearParam, clearAll }),
        [getValue, setValue, setValues, clearParam, clearAll],
    );

    return (
        <UrlStateContext.Provider value={contextValue}>
            {children}
        </UrlStateContext.Provider>
    );
};

const useUrlState = <T extends SerializableValue>(params: {
    key: string;
    defaultValue?: T;
    schema: ZodSchema<T>;
}): [T extends undefined ? T | undefined : T, (value: T) => void] => {
    const { key, defaultValue, schema } = params;
    const context = useContext(UrlStateContext);
    if (!context) {
        throw new Error("useUrlState must be used within a UrlStateProvider");
    }

    const rawValue = context.getValue<T>(key);

    // Apply schema validation if provided
    const value = useMemo(() => {
        if (schema && rawValue !== undefined) {
            const parsedValue = schema.safeParse(rawValue);
            if (!parsedValue.success) {
                // eslint-disable-next-line no-console
                console.warn(
                    `URL state validation failed for key "${key}" from value "${JSON.stringify(rawValue)}":`,
                    parsedValue.error,
                );
                return defaultValue;
            }
            return parsedValue.data;
        }
        return rawValue ?? defaultValue;
    }, [rawValue, schema, key, defaultValue]);

    const setValue = useCallback(
        (v: T) => {
            // Validate value before setting if schema is provided
            if (schema) {
                const validated = schema.parse(v);
                context.setValue<T>(key, validated);
                return;
            }
            context.setValue<T>(key, v);
        },
        [context, key, schema],
    );

    return [value, setValue] as [
        T extends undefined ? T | undefined : T,
        (value: T) => void,
    ];
};

const useUrlStateSetValues = () => {
    const setValues = useContext(UrlStateContext)?.setValues;
    if (!setValues) {
        throw new Error(
            "useUrlStateSetValues must be used within a UrlStateProvider",
        );
    }
    return setValues;
};

// Create convenience hooks with preset schema types
const useUrlStateBoolean = (key: string, defaultValue?: boolean) =>
    useUrlState({ key, defaultValue, schema: z.boolean() });

const useUrlStateNumber = (key: string, defaultValue?: number) =>
    useUrlState({ key, defaultValue, schema: z.number() });

const useUrlStateString = (key: string, defaultValue?: string) =>
    useUrlState({ key, defaultValue, schema: z.string() });

export {
    UrlStateContext,
    UrlStateProvider,
    useUrlState,
    useUrlStateBoolean,
    useUrlStateNumber,
    useUrlStateSetValues,
    useUrlStateString,
};
